/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, ReactElement, useContext, useRef } from "react";
import { Badge } from "@chakra-ui/react";
import { Box } from "@mui/material";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import axios from "axios";
import Card from "components/card";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ImSpinner8 } from "react-icons/im";
import { ProfileContext } from "../../../store/ProfileProvider";
import DisplayAmount from "../../../views/admin/shared/DisplayAmount";
import CommissionChart from "./components/CommissionChart";

dayjs.extend(utc);
dayjs.extend(timezone);

type Asset = {
  asset: string;
  available: string;
  pending: string;
  last7Days: string;
  thisMonth: string;
  lastMonth: string;
};

interface PayloadType {
  [key: string]: any;
}

type Credit = {
  data: string;
  description: string;
};

type RowData = {
  id: string;
  created_at: string;
  record_owner: string;
  credit: Credit;
  asset: string;
  status: string;
};

const customDataGridStyles: Partial<GridClasses> = {
  root: "mui-table-custom",
  columnHeader: "text-start text-md font-bold",
  row: "",
};

const rowPerPage = 5;

const timeFilters = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7Days" },
  { label: "Last 30 Days", value: "last30Days" },
  { label: "This Month", value: "thisMonth" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Custom Range", value: "customRange" },
];

const Dashboard = (userPermissions: any) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isChartLoading, setIsChartLoading] = useState<boolean>(false);
  const [agentCommisionEarned, setAgentCommisionEarned] = useState<any>([]);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("thisMonth");
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    new Date(),
  ]);
  const [commissionData, setCommissionData] = useState<any[]>([]);

  const agentId = localStorage.getItem("agentId");
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();

    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([
        getComissionEarned(signal),
        fetchCommissionHistory(signal),
      ]);
      if (signal.aborted) return;
      await fetchTransactions({ page: 0, limit: rowPerPage }, signal);
      if (signal.aborted) return;
      setIsLoading(false);
    };

    fetchData();
  }, []);

  // Calculate date range based on selected period
  const getDateRange = (period: string) => {
    const today = dayjs();
    switch (period) {
      case "today":
        return {
          from: today.format("YYYY-MM-DD 00:00"),
          to: today.format("YYYY-MM-DD 23:59"),
        };
      case "last7Days":
        return {
          from: today.subtract(7, "day").format("YYYY-MM-DD 00:00"),
          to: today.format("YYYY-MM-DD 23:59"),
        };
      case "last30Days":
        return {
          from: today.subtract(30, "day").format("YYYY-MM-DD 00:00"),
          to: today.format("YYYY-MM-DD 23:59"),
        };
      case "thisMonth":
        return {
          from: today.startOf("month").format("YYYY-MM-DD 00:00"),
          to: today.format("YYYY-MM-DD 23:59"),
        };
      case "lastMonth":
        return {
          from: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD 00:00"),
          to: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD 23:59"),
        };
      case "customRange":
        if (selectedDates.length === 2) {
          return {
            from: dayjs(selectedDates[0]).format("YYYY-MM-DD 00:00"),
            to: dayjs(selectedDates[1]).format("YYYY-MM-DD 23:59"),
          };
        }
        return null;
      default:
        return null;
    }
  };

  // Fetch commission history data
  const fetchCommissionHistory = async (signal?: AbortSignal) => {
    // Skip if customRange is selected but dates are incomplete
    if (selectedPeriod === "customRange" && selectedDates.length < 2) {
      return;
    }

    const dateRange = getDateRange(selectedPeriod);
    if (!dateRange) return;

    try {
      const queryObject: PayloadType = {
        transaction_type: "reseller_fee_received",
        id: agentId,
        type: "agent",
        from: dateRange.from,
        to: dateRange.to,
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-balance?${queryString}`,
        { signal }
      );

      const balances = response.data?.balances;
      if (balances) {
        setCommissionData(balances);
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      console.error("Error fetching commission data", error);
    }
  };

  // Fetch commission data when filter changes (after initial load)
  useEffect(() => {
    if (isLoading) return;

    const fetchData = async () => {
      setIsChartLoading(true);
      await fetchCommissionHistory();
      setIsChartLoading(false);
    };

    fetchData();
  }, [selectedPeriod, selectedDates]);

  const getComissionEarned = async (signal?: AbortSignal) => {
    try {
      const queryObject: PayloadType = {
        transaction_type: "reseller_fee_received",
        id: agentId,
        type: "agent",
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-balance?${queryString}`,
        { signal }
      );

      const balances = response.data?.balances;
      if (balances) {
        setAgentCommisionEarned(balances);
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      console.error("Error get balance data", error);
      return false;
    }
  };

  const fetchTransactions = async (
    payload: PayloadType = {},
    signal?: AbortSignal
  ) => {
    try {
      const queryObject: PayloadType = {
        limit: 5,
        page: payload?.page + 1,
        display_mode: "agent",
        transaction_type: "reseller_fee_received"
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/transaction/get-all-transactions?${queryString}`,
        { signal }
      );

      let newData = response.data.transactions;

      setRowCount(newData.length);

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      setTableData([]);
      console.error("Error fetching data", error);
    }
  };

  interface RecordStatus {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: RecordStatus[] = [
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "complete",
      value: "complete",
      display: (
        <Badge className={`${statusClasses} status-successful`}>
          Completed
        </Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "created_at",
      headerName: "Created Date",
      minWidth: 180,
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        formatDate(params.row.created_at),
    },
    {
      field: "record_owner",
      headerName: "",
      minWidth: 180,
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.record_owner,
    },
    {
      field: "credit",
      headerName: "Amount",
      minWidth: 180,
      sortable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
        <div>
          <div>{params.row.credit.data ? params.row.credit.data : ""}</div>
          <div style={{ fontSize: "0.85rem", color: "gray" }}>
            {params.row.credit.description ? params.row.credit.description : ""}
          </div>
        </div>
      ),
    },
    {
      field: "asset",
      headerName: "Asset",
      minWidth: 100,
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.asset,
    },
    {
      field: "status",
      headerName: "",
      minWidth: 200,
      sortable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => {
        const status = params.value as string;
        const statusItem = statusList.find((item) => item.status === status);
        return statusItem ? statusItem.display : null;
      },
    },
  ];

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM YY");
  };

  const filterColumns = () => {
    const excludeFields = ["merchant", "subaccount"];
    columns.filter((column) => !!!excludeFields.includes(column?.field));
    return columns.filter((column) => !!!excludeFields.includes(column?.field));
  };

  const setPaginationModel = (newValue: any) => {
    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      reseller_id: agentId,
    };

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        reseller_id: agentId,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
    } else {
      setPage(newValue.page);
    }

    fetchTransactions(newFilters);
  };

  const countNonZero = (data: Asset[]): Asset[] => {
    return data.filter((item) => parseFloat(item.available) > 0);
  };

  return (
    <div>
      <div className="mb-3 mt-3 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
          Commission Earned
        </h3>
      </div>

      {isLoading ? (
        <div className="mb-10 flex justify-center py-10">
          <ImSpinner8 className="spinner text-primary-500" size={28} />
        </div>
      ) : countNonZero(agentCommisionEarned).length > 0 ? (
        <div className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
          {countNonZero(agentCommisionEarned).map((item: any) => {
            const assetCode = item.asset.toLowerCase().includes("-")
              ? item.asset.toLowerCase().split("-")[0]
              : item.asset.toLowerCase();

            return (
              <div
                key={item.asset}
                className="
                  group relative cursor-pointer rounded-xl bg-white
                  p-3 shadow-sm ring-1
                  ring-gray-200 transition-all duration-200
                  hover:-translate-y-[2px] hover:shadow-lg
                  hover:ring-primary-500
                  sm:p-4
                  md:p-5
                "
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <span
                    className="
                      flex h-8 w-8 items-center justify-center rounded-full bg-gray-100
                      ring-1 ring-gray-200 sm:h-9 sm:w-9 md:h-10 md:w-10
                    "
                  >
                    <img
                      src={`/images/icons/${assetCode}.png`}
                      alt={item.asset}
                      className="sm:h-4.5 sm:w-4.5 h-4 w-4 md:h-5 md:w-5"
                    />
                  </span>

                  <span className="text-xs font-semibold text-navy-700 sm:text-sm">
                    {item.asset}
                  </span>
                </div>

                <div
                  className="
                    mt-2 text-lg font-semibold
                    tracking-tight text-navy-900 sm:mt-3
                    sm:text-xl md:mt-4 md:text-2xl
                  "
                >
                  {item.crypto ? (
                    item.available
                  ) : (
                    <DisplayAmount
                      amount={Number(item.available)}
                      currency={item.asset}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mb-10 flex flex-col items-center justify-center py-10 text-gray-500">
          <p className="text-sm">No balance available</p>
        </div>
      )}

      <div className="mb-3 mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
          Commission History
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
            {timeFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedPeriod(filter.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                  selectedPeriod === filter.value
                    ? "bg-white text-navy-700 shadow-sm"
                    : "text-gray-600 hover:text-navy-700"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {selectedPeriod === "customRange" && (
            <div className="w-[220px]">
              <RangeDatepicker
                selectedDates={selectedDates}
                onDateChange={setSelectedDates}
                propsConfigs={{
                  inputProps: {
                    className:
                      "h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-navy-700 shadow-sm outline-none",
                  },
                  dayOfMonthBtnProps: {
                    defaultBtnProps: {
                      borderColor: "rgb(233 236 239 / 1)",
                      borderRadius: "2px",
                      _hover: { background: "gray", color: "white" },
                    },
                    selectedBtnProps: {
                      background: "#293E9F",
                      color: "white",
                    },
                    isInRangeBtnProps: {
                      background: "#E8EAFF",
                      color: "#293E9F",
                    },
                  },
                  popoverCompProps: {
                    popoverContentProps: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
                    },
                  },
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mb-10 mt-4">
        <Card className="card w-full !rounded-xl p-6 !shadow-sm">
          <div className="flex min-h-[180px] w-full items-center justify-center">
            {isLoading || isChartLoading ? (
              <ImSpinner8 className="spinner text-primary-500" size={28} />
            ) : commissionData.length === 0 ? (
              <div className="text-sm text-gray-500">No data available</div>
            ) : (
              <CommissionChart
                balance={commissionData}
                selectedPeriod={selectedPeriod}
                selectedDates={selectedDates}
              />
            )}
          </div>
        </Card>
      </div>

      <div className="mb-3 mt-8 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
          Latest Commissions
        </h3>
      </div>
      <Box className="card mb-4 w-full">
        <DataGrid
          sx={{
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "8px 16px",
            },
            "& .MuiTablePagination-input": {
              marginRight: "-20px",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
          }}
          loading={isLoading}
          rows={tableData || []}
          columns={filterColumns()}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          sortingMode="server"
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          rowHeight={48}
          columnHeaderHeight={48}
          paginationModel={{ page: 0, pageSize: rowPerPage }}
          onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          autoHeight
          classes={customDataGridStyles}
          hideFooter={true}
          localeText={{
            noRowsLabel: "No data available",
          }}
        />
      </Box>
    </div>
  );
};

export default Dashboard;
